import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShoppingBag, Wine, Package, MapPin, Calendar, Filter } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

export default function B2BLotsMarketplace() {
  const [lots, setLots] = useState([]);
  const [filter, setFilter] = useState("all"); // all | wine | foodbox
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyLot, setBuyLot] = useState(null);
  const [qty, setQty] = useState(0);
  const [notes, setNotes] = useState("");
  const [buying, setBuying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?product_type=${filter}` : "";
      const r = await api.get(`/b2b/lots/marketplace${params}`);
      setLots(r.data.lots || []);
    } catch {
      toast.error("Errore caricamento marketplace");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openBuy = (lot) => { setBuyLot(lot); setQty(lot.min_order_qty); setNotes(""); setBuyOpen(true); };

  const confirmBuy = async () => {
    setBuying(true);
    try {
      await api.post(`/b2b/lots/${buyLot.id}/buy`, { qty: Number(qty), notes });
      toast.success("Richiesta acquisto inviata. Il venditore confermerà l'ordine.");
      setBuyOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore acquisto");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div data-testid="b2b-marketplace-page" className="container-xl py-10">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingBag className="h-6 w-6 text-[hsl(var(--accent))]" />
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold">Marketplace B2B</div>
      </div>
      <h1 className="editorial-heading text-4xl md:text-5xl mb-2">Acquista lotti vini · food box</h1>
      <p className="text-muted-foreground mb-6">Lotti approvati pubblicati da altri rivenditori certificati Amazonico 3000.</p>

      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="marketplace-filters">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {[
          { v: "all", label: "Tutti" }, { v: "wine", label: "Vini" }, { v: "foodbox", label: "Food Box" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`filter-${f.v}`}
            className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
              filter === f.v ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-transparent border-border text-foreground hover:border-[hsl(var(--primary))]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Caricamento lotti…</div>
      ) : lots.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Package className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <div className="text-muted-foreground">Nessun lotto disponibile in questa categoria.</div>
          <Link to="/b2b/lots/sell" className="inline-block mt-4 text-[hsl(var(--accent))] hover:underline">Sii il primo a pubblicare →</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketplace-grid">
          {lots.map((lot) => (
            <article key={lot.id} className="bg-card border border-border rounded-sm overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-[4/3] bg-[hsl(var(--muted))] relative">
                {lot.image_url ? (
                  <img src={lot.image_url} alt={lot.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {lot.product_type === "wine" ? <Wine className="h-16 w-16 text-muted-foreground/30" /> : <Package className="h-16 w-16 text-muted-foreground/30" />}
                  </div>
                )}
                <span className={`absolute top-3 left-3 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full ${
                  lot.product_type === "wine" ? "bg-[#7A1C2B] text-white" : "bg-[#C9A961] text-black"
                }`}>{lot.product_type === "wine" ? "Vino" : "Food Box"}</span>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-lg mb-1 line-clamp-2">{lot.title}</h3>
                <div className="text-xs text-muted-foreground mb-3">{lot.seller_company}</div>
                {lot.product_type === "wine" && (lot.region || lot.vintage) && (
                  <div className="text-xs text-muted-foreground mb-2">{[lot.region, lot.vintage].filter(Boolean).join(" · ")}</div>
                )}
                <div className="flex items-center justify-between mb-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Prezzo unitario</div>
                    <div className="text-lg font-bold text-[hsl(var(--accent))]">€ {lot.price_per_unit?.toFixed(2)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-muted-foreground">Disponibili</div>
                    <div className="font-semibold">{lot.quantity}</div>
                  </div>
                </div>
                {lot.location && (
                  <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><MapPin className="h-3 w-3" /> {lot.location}</div>
                )}
                {lot.available_until && (
                  <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1"><Calendar className="h-3 w-3" /> Valido fino al {new Date(lot.available_until).toLocaleDateString("it-IT")}</div>
                )}
                <button
                  onClick={() => openBuy(lot)}
                  data-testid={`buy-lot-${lot.id}`}
                  className="w-full px-4 py-2.5 bg-[hsl(var(--primary))] text-white text-sm font-semibold rounded-sm hover:bg-[hsl(var(--primary))]/90 transition-colors"
                >
                  Richiedi acquisto
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Richiesta acquisto lotto</DialogTitle></DialogHeader>
          {buyLot && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <div><strong>{buyLot.title}</strong></div>
                <div>Venditore: {buyLot.seller_company}</div>
                <div>Min ordine: {buyLot.min_order_qty} · Disponibili: {buyLot.quantity}</div>
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Quantità</span>
                <input type="number" min={buyLot.min_order_qty} max={buyLot.quantity} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="buy-qty" className="w-full px-3 py-2 border border-border rounded-sm" />
              </label>
              <div className="text-sm">Totale richiesto: <strong>€ {((Number(qty) || 0) * buyLot.price_per_unit).toFixed(2)}</strong></div>
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Note al venditore</span>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="buy-notes" />
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setBuyOpen(false)} className="px-4 py-2 text-sm text-muted-foreground">Annulla</button>
            <button onClick={confirmBuy} disabled={buying} data-testid="buy-confirm" className="px-4 py-2 bg-[hsl(var(--primary))] text-white text-sm font-semibold rounded-sm disabled:opacity-50">
              {buying ? "Invio…" : "Conferma richiesta"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
